import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { libraryService, materialService } from '../services/supabase';
import { Library, Material } from '../types/models';
import { FiSave, FiX, FiArrowLeft, FiCheck, FiPlusCircle, FiSearch, FiBook, FiVideo, FiFileText, FiLock, FiUsers } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { normalizeImageUrl } from '../utils/imageUtils';
import UserAccessManager from '../components/library/UserAccessManager';

interface LibraryFormData {
  nome: string;
  descricao: string;
  is_public: boolean;
  cor: string;
}

const LibraryForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState<LibraryFormData>({
    nome: '',
    descricao: '',
    is_public: true,
    cor: 'bg-vermelho-escuro'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);
  
  // Estado para gerenciar os materiais
  const [availableMaterials, setAvailableMaterials] = useState<Material[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<Material[]>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isEditMode) {
      fetchLibrary();
    }
    
    fetchAvailableMaterials();
  }, [id, isEditMode]);
  
  // Busca a biblioteca para edição
  const fetchLibrary = async () => {
    try {
      setFetchLoading(true);
      const { data, error } = await libraryService.getLibraryById(id!);
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (!data) {
        throw new Error('Biblioteca não encontrada');
      }
      
      setFormData({
        nome: data.nome,
        descricao: data.descricao,
        is_public: data.is_public,
        cor: data.cor || 'bg-vermelho-escuro'
      });
      
      // Busca os materiais associados à biblioteca
      fetchLibraryMaterials();
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados da biblioteca');
      console.error('Erro ao buscar biblioteca:', err);
    } finally {
      setFetchLoading(false);
    }
  };
  
  // Busca os materiais disponíveis
  const fetchAvailableMaterials = async () => {
    try {
      setLoadingMaterials(true);
      const { data, error } = await materialService.getMaterials();
      
      if (error) {
        throw new Error(error.message);
      }
      
      setAvailableMaterials(data || []);
    } catch (err: any) {
      console.error('Erro ao buscar materiais disponíveis:', err);
    } finally {
      setLoadingMaterials(false);
    }
  };
  
  // Busca os materiais já associados à biblioteca
  const fetchLibraryMaterials = async () => {
    if (!id) return;
    
    try {
      setLoadingMaterials(true);
      const { data, error } = await libraryService.getLibraryMaterials(id);
      
      if (error) {
        throw new Error(error.message);
      }
      
      console.log('Resposta de getLibraryMaterials:', data);
      
      // Extrai os materiais da resposta
      if (data && data.length > 0) {
        // Filtra para remover itens nulos ou indefinidos
        const libraryMaterials = data
          .map((item: any) => item.materiais)
          .filter((material: any) => material !== null && material !== undefined);
          
        console.log('Materiais processados:', libraryMaterials);
        setSelectedMaterials(libraryMaterials);
      } else {
        setSelectedMaterials([]);
      }
    } catch (err: any) {
      console.error('Erro ao buscar materiais da biblioteca:', err);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleSelectColor = (color: string) => {
    setFormData(prev => ({ ...prev, cor: color }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Prepara os dados para envio
      const libraryData = {
        ...formData,
        created_by: user?.id
      };
      
      let response;
      let libraryId = id;
      
      if (isEditMode) {
        // Atualiza a biblioteca existente
        response = await libraryService.updateLibrary(id!, libraryData);
      } else {
        // Cria uma nova biblioteca
        response = await libraryService.createLibrary(libraryData);
        // Pega o ID da biblioteca recém-criada para associar materiais
        if (response.data) {
          // Usando asserção de tipo para evitar erros de TypeScript
          const libraryDataArray = response.data as any[];
          if (libraryDataArray.length > 0 && libraryDataArray[0].id) {
            libraryId = libraryDataArray[0].id;
            console.log('Nova biblioteca criada com ID:', libraryId);
          }
        }
      }
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      // Associa os materiais selecionados à biblioteca
      if (libraryId) {
        console.log('Salvando materiais para a biblioteca ID:', libraryId);
        
        // Se estiver em modo de edição, primeiro buscamos os materiais atuais
        // para determinar o que adicionar/remover
        if (isEditMode) {
          const { data: currentMaterialsData } = await libraryService.getLibraryMaterials(libraryId);
          const currentMaterialIds = currentMaterialsData
            ? currentMaterialsData.map((item: any) => item.materiais?.id).filter(Boolean)
            : [];
            
          console.log('Materiais atuais da biblioteca:', currentMaterialIds);
          
          // Materiais selecionados IDs
          const selectedMaterialIds = selectedMaterials.map(material => material.id);
          console.log('Materiais selecionados para salvar:', selectedMaterialIds);
          
          // Materiais para remover (estão na biblioteca mas não na seleção atual)
          const materialsToRemove = currentMaterialIds.filter(
            id => !selectedMaterialIds.includes(id)
          );
          
          // Remove os materiais não selecionados
          for (const materialId of materialsToRemove) {
            console.log('Removendo material ID:', materialId);
            await libraryService.removeMaterialFromLibrary(libraryId, materialId);
          }
          
          // Materiais para adicionar (estão na seleção mas não na biblioteca)
          const materialsToAdd = selectedMaterialIds.filter(
            id => !currentMaterialIds.includes(id)
          );
          
          // Adiciona os novos materiais
          for (const materialId of materialsToAdd) {
            console.log('Adicionando material ID:', materialId);
            await libraryService.addMaterialToLibrary(libraryId, materialId);
          }
        } else {
          // Para nova biblioteca, simplesmente adiciona todos os materiais selecionados
          for (const material of selectedMaterials) {
            console.log('Adicionando material à nova biblioteca:', material.id);
            await libraryService.addMaterialToLibrary(libraryId, material.id);
          }
        }
      }
      
      setSuccess(true);
      
      // Redirecionar após sucesso
      setTimeout(() => {
        navigate('/bibliotecas');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar biblioteca');
      console.error('Erro ao salvar biblioteca:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Filtra os materiais disponíveis por termo de busca
  const filteredMaterials = availableMaterials.filter(material => 
    !selectedMaterials.some(selected => selected.id === material.id) && // Não está selecionado
    (material.titulo.toLowerCase().includes(searchTerm.toLowerCase()) || 
     material.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  // Renderiza o ícone baseado no tipo de material
  const renderMaterialIcon = (tipo: string) => {
    switch (tipo) {
      case 'vídeo':
        return <FiVideo className="text-blue-400" />;
      case 'livro':
        return <FiBook className="text-green-400" />;
      case 'revista':
        return <FiFileText className="text-purple-400" />;
      default:
        return <FiBook />;
    }
  };

  if (fetchLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-vermelho-claro"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-6 flex items-center">
        <Link to="/bibliotecas" className="mr-4 text-gray-400 hover:text-white transition-colors">
          <FiArrowLeft size={20} />
        </Link>
        <h1 className="text-2xl md:text-3xl font-bold text-white font-raleway">
          {isEditMode ? 'Editar' : 'Nova'} <span className="text-vermelho-claro">Biblioteca</span>
        </h1>
      </div>
      
      {error && (
        <div className="bg-red-900/30 border border-red-800 rounded-md p-3 mb-6 flex items-center text-red-200 animate-fade-in">
          <FiX className="mr-2 flex-shrink-0 text-red-400" />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-900/30 border border-green-800 rounded-md p-3 mb-6 flex items-center text-green-200 animate-fade-in">
          <FiSave className="mr-2 flex-shrink-0 text-green-400" />
          <span>Biblioteca {isEditMode ? 'atualizada' : 'criada'} com sucesso! Redirecionando...</span>
        </div>
      )}
      
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Coluna da esquerda - Informações básicas */}
            <div className="space-y-6">
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-300 mb-1">
                  Nome da Biblioteca <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  required
                  value={formData.nome}
                  onChange={handleChange}
                  className="input"
                  placeholder="Ex: Matemática Básica, Ciências, etc."
                />
              </div>
              
              <div>
                <label htmlFor="descricao" className="block text-sm font-medium text-gray-300 mb-1">
                  Descrição <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="descricao"
                  name="descricao"
                  required
                  value={formData.descricao}
                  onChange={handleChange}
                  className="input min-h-[100px]"
                  placeholder="Breve descrição sobre o conteúdo da biblioteca"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Cor <span className="text-gray-500 text-xs ml-2">(Opcional)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {['bg-vermelho-escuro', 'bg-blue-600', 'bg-green-600', 'bg-purple-600', 'bg-yellow-600', 'bg-orange-600'].map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full ${color} ${formData.cor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''}`}
                      onClick={() => handleSelectColor(color)}
                    >
                      {formData.cor === color && <FiCheck className="text-white mx-auto" />}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_public"
                  name="is_public"
                  checked={formData.is_public}
                  onChange={handleChange}
                  className="w-4 h-4 bg-gray-800 border-gray-700 rounded"
                />
                <label htmlFor="is_public" className="ml-2 text-sm font-medium text-gray-300">
                  Biblioteca Pública <span className="text-gray-500 text-xs">(Visível para todos os usuários)</span>
                </label>
              </div>
            </div>
            
            {/* Coluna da direita - Materiais da biblioteca */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Materiais na Biblioteca
              </label>
              
              <div className="border border-gray-700 rounded-lg overflow-hidden">
                {/* Lista de materiais selecionados */}
                <div className="max-h-[300px] overflow-y-auto p-3 space-y-2 bg-gray-800/50">
                  {selectedMaterials.length === 0 ? (
                    <div className="text-center py-4 text-gray-400 text-sm">
                      Ainda não há materiais nesta biblioteca
                    </div>
                  ) : (
                    selectedMaterials.map(material => (
                      <div 
                        key={material.id} 
                        className="flex items-center justify-between bg-gray-800 p-2 rounded"
                      >
                        <div className="flex items-center">
                          {renderMaterialIcon(material.tipo)}
                          <span className="ml-2 text-gray-200">{material.titulo}</span>
                        </div>
                        <button
                          type="button"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => {
                            setSelectedMaterials(prev => prev.filter(m => m.id !== material.id));
                          }}
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                
                {/* Barra de busca para adicionar materiais */}
                <div className="p-3 border-t border-gray-700">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiSearch className="text-gray-500" />
                    </div>
                    <input
                      type="text"
                      placeholder="Buscar materiais para adicionar..."
                      className="input pl-10"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  
                  {/* Lista de resultados da busca */}
                  {searchTerm && (
                    <div className="mt-2 max-h-[150px] overflow-y-auto border border-gray-700 rounded bg-gray-800/50">
                      {filteredMaterials.length === 0 ? (
                        <div className="text-center py-3 text-gray-400 text-sm">
                          Nenhum material encontrado
                        </div>
                      ) : (
                        filteredMaterials.map(material => (
                          <div 
                            key={material.id} 
                            className="flex items-center justify-between p-2 hover:bg-gray-800 cursor-pointer"
                            onClick={() => {
                              setSelectedMaterials(prev => [...prev, material]);
                              setSearchTerm('');
                            }}
                          >
                            <div className="flex items-center">
                              {renderMaterialIcon(material.tipo)}
                              <span className="ml-2 text-gray-200">{material.titulo}</span>
                            </div>
                            <FiPlusCircle className="text-blue-400" />
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Seção de gerenciamento de acesso (apenas para bibliotecas privadas) */}
          {isEditMode && !formData.is_public && (
            <div className="mt-8 border-t border-gray-700 pt-6">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-white flex items-center">
                  <FiUsers className="mr-2 text-vermelho-claro" />
                  Gerenciar Acesso
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  Esta é uma biblioteca privada. Adicione usuários específicos que podem acessá-la.
                </p>
              </div>
              
              <UserAccessManager libraryId={id!} />
            </div>
          )}
          
          {/* Botões de ação */}
          <div className="mt-8 flex justify-end space-x-4">
            <Link
              to="/bibliotecas"
              className="btn btn-secondary"
            >
              <FiX className="mr-2" />
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={loading}
              className={`btn btn-primary ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
              ) : (
                <FiSave className="mr-2" />
              )}
              {isEditMode ? 'Atualizar' : 'Salvar'} Biblioteca
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default LibraryForm; 